import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2 } from 'lucide-react';

import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../ui/form';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

type DuplicateOption = {
  label: string;
  value: string;
};

export type SubprojectDuplicateItemForm = {
  targetRoomId: string;
  categoryId: string;
  quantity: number;
};

interface SubprojectDuplicateItemDialogProps {
  open: boolean;
  isSubmitting?: boolean;
  sourceRoomId: string;
  sourceRoomName: string;
  sourceCategoryId: string;
  sourceCategoryName?: string | null;
  itemRef: string;
  roomOptions: DuplicateOption[];
  categoryOptions: DuplicateOption[];
  noRoomOptionsMessage?: string;
  onClose: () => void;
  onSubmit: (values: SubprojectDuplicateItemForm) => Promise<void> | void;
}

export function SubprojectDuplicateItemDialog({
  open,
  isSubmitting = false,
  sourceRoomId,
  sourceRoomName,
  sourceCategoryId,
  sourceCategoryName,
  itemRef,
  roomOptions,
  categoryOptions,
  noRoomOptionsMessage,
  onClose,
  onSubmit,
}: SubprojectDuplicateItemDialogProps) {
  const hasRoomOptions = roomOptions.length > 0;
  const hasCategoryOptions = categoryOptions.length > 0;
  const defaultTargetRoomId = useMemo(
    () =>
      roomOptions.some((option) => option.value === sourceRoomId)
        ? sourceRoomId
        : (roomOptions[0]?.value ?? ''),
    [roomOptions, sourceRoomId],
  );

  const defaultValues = useMemo<SubprojectDuplicateItemForm>(
    () => ({
      targetRoomId: defaultTargetRoomId,
      categoryId: categoryOptions[0]?.value ?? '',
      quantity: 1,
    }),
    [categoryOptions, defaultTargetRoomId],
  );

  const form = useForm<SubprojectDuplicateItemForm>({
    defaultValues,
  });

  useEffect(() => {
    if (!open) return;
    form.reset(defaultValues);
  }, [open, defaultValues, form]);

  const handleSubmit = form.handleSubmit(async (values) => {
    await onSubmit({
      targetRoomId: values.targetRoomId,
      categoryId: values.categoryId,
      quantity: Number(values.quantity) || 0,
    });
  });

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen && !isSubmitting) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Duplicar item {itemRef ? `(${itemRef})` : ''}</DialogTitle>
          <DialogDescription>
            Crea una copia en la estancia que elijas. Debes seleccionar una categoría distinta a la actual.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
              Estancia actual: <span className="font-medium text-gray-900">{sourceRoomName}</span>
              {' · '}Categoría actual:{' '}
              <span className="font-medium text-gray-900">{sourceCategoryName ?? 'Sin categoría'}</span>
            </div>

            <FormField
              control={form.control}
              name="targetRoomId"
              rules={{
                required: 'Selecciona una estancia destino.',
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Estancia destino</FormLabel>
                  <FormControl>
                    <Select
                      value={field.value}
                      onValueChange={(value) => field.onChange(value)}
                      disabled={isSubmitting || !hasRoomOptions}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona una estancia" />
                      </SelectTrigger>
                      <SelectContent>
                        {roomOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  {!hasRoomOptions ? (
                    <p className="text-xs text-amber-700">
                      {noRoomOptionsMessage ?? 'No hay estancias disponibles para duplicar este item.'}
                    </p>
                  ) : null}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="categoryId"
              rules={{
                required: 'Selecciona una categoría destino.',
                validate: (value) =>
                  value !== sourceCategoryId || 'La categoría destino debe ser distinta de la categoría actual.',
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Categoría destino</FormLabel>
                  <FormControl>
                    <Select
                      value={field.value}
                      onValueChange={(value) => field.onChange(value)}
                      disabled={isSubmitting || !hasCategoryOptions}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona una categoría" />
                      </SelectTrigger>
                      <SelectContent>
                        {categoryOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  {!hasCategoryOptions ? (
                    <p className="text-xs text-amber-700">
                      No hay categorías activas disponibles distintas de la categoría actual.
                    </p>
                  ) : null}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="quantity"
              rules={{
                required: 'Indica una cantidad.',
                validate: (value) =>
                  Number.isInteger(Number(value)) && Number(value) > 0
                    ? true
                    : 'La cantidad debe ser un entero mayor que 0.',
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Unidades</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={1}
                      step={1}
                      value={field.value}
                      disabled={isSubmitting}
                      onChange={(event) => field.onChange(Number(event.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button variant="outline" type="button" onClick={onClose} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !hasRoomOptions || !hasCategoryOptions}
              >
                {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Duplicar
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
